import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp } from "@/lib/format";
import { Card, Field, MoneyInput, BigButton } from "@/components/admin/common";
import { TYPE_LABEL } from "@/components/finance/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { code: "", name: "", type: "beban", is_cash: false, omzet: false, active: true, opening_balance: 0 };

export default function Accounts({ accounts, reload }) {
  const [form, setForm] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, opening_balance: Number(form.opening_balance) || 0 };
    try {
      if (form.created_at) await api.put(`/admin/finance/accounts/${form.code}`, body);
      else await api.post("/admin/finance/accounts", body);
      toast.success("Akun tersimpan");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const quickToggle = async (a) => { await api.put(`/admin/finance/accounts/${a.code}`, { ...a, active: !a.active }); reload(); };
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  return (
    <div className="space-y-4" data-testid="finance-accounts">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600 max-w-xl">Daftar Akun (COA) standar SAK EMKM. Akun bertanda <b>Kas/Bank</b> muncul sebagai rekening di form transaksi. Saldo awal diisi sesuai sisi normal akun.</p>
        <BigButton onClick={() => setForm(EMPTY)} data-testid="add-account-btn"><Plus className="w-4 h-4" />Tambah akun</BigButton>
      </div>
      {Object.keys(TYPE_LABEL).map((t) => (
        <Card key={t} className="overflow-hidden">
          <p className="px-4 py-2 bg-soft font-extrabold text-sm">{TYPE_LABEL[t]}</p>
          {accounts.filter((a) => a.type === t).map((a) => (
            <div key={a.code} className={`flex items-center gap-3 px-4 py-2.5 border-t border-border ${a.active ? "" : "opacity-50"}`} data-testid={`account-row-${a.code}`}>
              <button onClick={() => setForm(a)} className="flex-1 min-w-0 text-left" data-testid={`account-edit-${a.code}`}>
                <p className="font-semibold truncate"><span className="text-slate-400 mr-2 tabular-nums">{a.code}</span>{a.name}</p>
                <p className="text-xs text-slate-500">{a.is_cash && "Kas/Bank · "}{a.omzet && "Omzet (peredaran bruto) · "}Saldo awal {rp(a.opening_balance)}</p>
              </button>
              <Switch checked={a.active} onCheckedChange={() => quickToggle(a)} data-testid={`account-active-${a.code}`} />
            </div>
          ))}
        </Card>
      ))}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="font-admin" data-testid="account-form-dialog">
          <DialogHeader><DialogTitle>{form?.created_at ? "Ubah akun" : "Tambah akun"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <Field label="Kode akun"><Input required disabled={!!form.created_at} value={form.code} onChange={set("code")} placeholder="6-2100" className="h-12" data-testid="account-code-input" /></Field>
              <Field label="Kelompok">
                <Select value={form.type} onValueChange={set("type")}>
                  <SelectTrigger className="h-12" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Nama akun" className="col-span-2"><Input required value={form.name} onChange={set("name")} className="h-12" data-testid="account-name-input" /></Field>
              <Field label="Saldo awal" className="col-span-2"><MoneyInput value={form.opening_balance} onChange={set("opening_balance")} testid="account-opening-input" /></Field>
              {form.type === "aset" && <label className="col-span-2 flex items-center justify-between gap-3 text-sm font-semibold">Rekening Kas / Bank<Switch checked={form.is_cash} onCheckedChange={set("is_cash")} data-testid="account-cash-switch" /></label>}
              {form.type === "pendapatan" && <label className="col-span-2 flex items-center justify-between gap-3 text-sm font-semibold">Dihitung sebagai omzet (PPh Final)<Switch checked={form.omzet} onCheckedChange={set("omzet")} data-testid="account-omzet-switch" /></label>}
              <label className="col-span-2 flex items-center justify-between gap-3 text-sm font-semibold">Aktif<Switch checked={form.active} onCheckedChange={set("active")} data-testid="account-active-switch" /></label>
              <BigButton type="submit" className="col-span-2" data-testid="account-save-btn">Simpan akun</BigButton>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
